import React, { useState, useMemo } from "react";
import {
  Box,
  VStack,
  Select,
  Button,
  Input,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Divider,
} from "@chakra-ui/react";
import { AddIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { ColumnType, AggregationType, aggregationTypeToJSON } from "build-ts/enums";
import { ColumnInfo, Header, ObservationColumnProto } from "build-ts/metadata";

interface ObservationProps {
  modelName?: string;
  columns: ColumnInfo[];
  selectedObservationColumn: Header | null;
  selectedAggregation?: AggregationType;
  selectedObservationColumns: ObservationColumnProto[];
  handleObservationColumnChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handleAggregationChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handleAddObservationColumn: () => void;
  handleDeleteObservationColumn: (index: number) => void;
  compact?: boolean;
}

const Observation: React.FC<ObservationProps> = ({
  modelName,
  columns,
  selectedObservationColumn,
  selectedAggregation,
  selectedObservationColumns,
  handleObservationColumnChange,
  handleAggregationChange,
  handleAddObservationColumn,
  handleDeleteObservationColumn,
  compact,
}) => {
  const sizing = compact
    ? { fs: "xs", vs: 3, controlSize: "sm" as const }
    : { fs: "sm", vs: 4, controlSize: "md" as const };
  const { fs, vs, controlSize } = sizing;

  const [columnSearchTerm, setColumnSearchTerm] = useState("");

  const measureColumns = useMemo(() => columns?.filter(c => c.column_type === ColumnType.MEASURE) ?? [], [columns]);

  const filteredMeasureColumns = useMemo(() => {
    if (!columnSearchTerm.trim()) return measureColumns;
    return measureColumns.filter(c => c.header?.name?.toLowerCase().includes(columnSearchTerm.toLowerCase()));
  }, [measureColumns, columnSearchTerm]);

  const makeSelectEvent = (value: string): React.ChangeEvent<HTMLSelectElement> =>
    ({ target: { value } }) as React.ChangeEvent<HTMLSelectElement>;

  return (
    <VStack spacing={vs} align="stretch" width="100%">
      <Text fontSize={fs} fontWeight="700" color="gray.700" mb={0}>
        Aggregation
      </Text>

      {/* Searchable column dropdown - MEASURE columns only */}
      <Menu>
        <MenuButton
          as={Button}
          width="100%"
          size={controlSize}
          rightIcon={<ChevronDownIcon />}
          fontSize={fs}
          bg="white"
          border="1px"
          borderColor="gray.300"
          borderRadius="md"
          fontWeight="normal"
          _hover={{ bg: "purple.50" }}
          textAlign="left"
          color={selectedObservationColumn ? "gray.800" : "gray.400"}
        >
          {selectedObservationColumn?.name ?? "Select Column"}
        </MenuButton>
        <MenuList maxHeight="260px" overflowY="auto" zIndex={10}>
          <Box p={2} pb={1}>
            <Input
              placeholder="Search columns..."
              value={columnSearchTerm}
              onChange={e => setColumnSearchTerm(e.target.value)}
              size="sm"
              borderColor="gray.300"
              _focus={{ borderColor: "purple.400" }}
              onClick={e => e.stopPropagation()}
            />
          </Box>
          {filteredMeasureColumns.map(col => (
            <MenuItem
              key={col.header?.id}
              onClick={() => {
                handleObservationColumnChange(makeSelectEvent(col.header?.id ?? ""));
                setColumnSearchTerm("");
              }}
              fontSize={fs}
              bg={selectedObservationColumn?.id === col.header?.id ? "purple.50" : undefined}
            >
              {col.header?.name}
            </MenuItem>
          ))}
          {filteredMeasureColumns.length === 0 && (
            <Box p={2} color="gray.500" fontSize={fs}>
              No columns found
            </Box>
          )}
        </MenuList>
      </Menu>

      {/* Aggregation function select */}
      <Select
        placeholder="Aggregation Function"
        onChange={handleAggregationChange}
        value={selectedAggregation !== undefined ? aggregationTypeToJSON(selectedAggregation) : ""}
        fontSize={fs}
        size={controlSize}
        bg="white"
        borderColor="gray.300"
      >
        <option value="SUM">SUM</option>
        <option value="AVERAGE">AVERAGE</option>
        <option value="COUNT">COUNT</option>
        <option value="UNIQUE_COUNT">UNIQUE_COUNT</option>
        <option value="GROWTH">GROWTH</option>
        <option value="MIN">MIN</option>
        <option value="MAX">MAX</option>
        <option value="NO_AGGREGATION">NO_AGGREGATION</option>
      </Select>

      <Button
        colorScheme="purple"
        onClick={handleAddObservationColumn}
        w="100%"
        size={controlSize}
        leftIcon={<AddIcon boxSize="0.9rem" />}
        fontWeight="bold"
        isDisabled={selectedObservationColumn === null || selectedAggregation === undefined}
      >
        Include
      </Button>

      {/* Added observations as purple tags */}
      {selectedObservationColumns.length > 0 && (
        <>
          <Divider />
          <Wrap spacing={2}>
            {selectedObservationColumns.map((obs, index) => (
              <WrapItem key={index}>
                <Tag
                  size="sm"
                  fontSize="xs"
                  bg="purple.50"
                  border="1px"
                  borderColor="purple.300"
                  color="gray.800"
                  py={1}
                >
                  <TagLabel>
                    {aggregationTypeToJSON(obs.aggregation!).toUpperCase()}({obs.logical_column_header?.name})
                  </TagLabel>
                  <TagCloseButton onClick={() => handleDeleteObservationColumn(index)} />
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        </>
      )}

      {modelName && (
        <VStack align="flex-start" spacing={1} w="100%">
          <Text fontSize={fs} fontWeight="700" color="gray.700" mb={0}>
            From data model
          </Text>
          <Tag size={controlSize} colorScheme="purple" variant="outline" maxW="100%" borderRadius="md">
            <TagLabel whiteSpace="normal" wordBreak="break-word">
              {modelName}
            </TagLabel>
          </Tag>
        </VStack>
      )}
    </VStack>
  );
};

export default Observation;
